import { Button } from './ui/button';
import { List, GitBranch, BarChart3, LayoutDashboard, Lightbulb, FileText, Network, Boxes, Calculator, Ruler, AppWindow, Gauge, LucideIcon } from 'lucide-react';

export type ViewMode = 'table' | 'tree';
export type AnalyticsViewMode = 'charts' | 'dashboards';
export type ContextViewMode = 'analytics' | 'company';
export type SemanticLayerTab = 'diagram' | 'entities' | 'metrics' | 'dimensions';
export type UsageAnalyticsTab = 'embed-sessions' | 'ellie-usage';

interface ToggleOption<T extends string> {
  value: T;
  label: string;
  icon: LucideIcon;
}

interface GenericToggleProps<T extends string> {
  value: T;
  onChange: (mode: T) => void;
  options: ToggleOption<T>[];
}

export function GenericToggle<T extends string>({ value, onChange, options }: GenericToggleProps<T>) {
  return (
    <div className="flex items-center gap-0.5 border rounded-md p-1">
      {options.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'default' : 'ghost'}
          size="sm"
          onClick={() => onChange(option.value)}
          className="gap-2 h-8"
        >
          <option.icon className="h-4 w-4" />
          {option.label}
        </Button>
      ))}
    </div>
  );
}

interface ViewModeToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}

export function ViewModeToggle({ value, onChange }: ViewModeToggleProps) {
  const options: [ToggleOption<ViewMode>, ToggleOption<ViewMode>] = [
    { value: 'table', label: 'Table', icon: List },
    { value: 'tree', label: 'Tree', icon: GitBranch },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface AnalyticsViewToggleProps {
  value: AnalyticsViewMode;
  onChange: (mode: AnalyticsViewMode) => void;
}

export function AnalyticsViewToggle({ value, onChange }: AnalyticsViewToggleProps) {
  const options: [ToggleOption<AnalyticsViewMode>, ToggleOption<AnalyticsViewMode>] = [
    { value: 'charts', label: 'Charts', icon: BarChart3 },
    { value: 'dashboards', label: 'Dashboards', icon: LayoutDashboard },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

interface ContextViewToggleProps {
  value: ContextViewMode;
  onChange: (mode: ContextViewMode) => void;
}

export function ContextViewToggle({ value, onChange }: ContextViewToggleProps) {
  const options: [ToggleOption<ContextViewMode>, ToggleOption<ContextViewMode>] = [
    { value: 'analytics', label: 'Analytics Context', icon: Lightbulb },
    { value: 'company', label: 'Company Context', icon: FileText },
  ];
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}

export const SEMANTIC_LAYER_TABS: ToggleOption<SemanticLayerTab>[] = [
  { value: 'diagram', label: 'Diagram', icon: Network },
  { value: 'entities', label: 'Entities', icon: Boxes },
  { value: 'metrics', label: 'Metrics', icon: Calculator },
  { value: 'dimensions', label: 'Dimensions', icon: Ruler },
];

export function semanticLayerTabLabel(tab: SemanticLayerTab): string {
  return SEMANTIC_LAYER_TABS.find((t) => t.value === tab)?.label ?? '';
}

interface SemanticLayerTabsProps {
  value: SemanticLayerTab;
  onChange: (tab: SemanticLayerTab) => void;
}

export function SemanticLayerTabs({ value, onChange }: SemanticLayerTabsProps) {
  return <GenericToggle value={value} onChange={onChange} options={SEMANTIC_LAYER_TABS} />;
}

export const USAGE_ANALYTICS_TABS: ToggleOption<UsageAnalyticsTab>[] = [
  { value: 'embed-sessions', label: 'Embed Sessions', icon: AppWindow },
  { value: 'ellie-usage', label: 'Ellie Usage', icon: Gauge },
];

export function usageAnalyticsTabLabel(tab: UsageAnalyticsTab): string {
  return USAGE_ANALYTICS_TABS.find((t) => t.value === tab)?.label ?? '';
}

interface UsageAnalyticsTabsProps {
  value: UsageAnalyticsTab;
  onChange: (tab: UsageAnalyticsTab) => void;
  /** Tabs to show; defaults to all of them. */
  options?: ToggleOption<UsageAnalyticsTab>[];
}

export function UsageAnalyticsTabs({ value, onChange, options = USAGE_ANALYTICS_TABS }: UsageAnalyticsTabsProps) {
  return <GenericToggle value={value} onChange={onChange} options={options} />;
}
